import {
  CardChangeEvent,
  ChangeEvent,
  ICardExpirationDateElement,
  ICardNumberElement,
  ICardVerificationCodeElement,
  useBasisTheory,
} from '@basis-theory/react-elements'
import { zodResolver } from '@hookform/resolvers/zod'
import React, { useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import CardCvcInput from './card-cvc-input'
import CardExpiryInput from './card-expiry-input'
import CardNumberInput from './card-number-input'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { CardBrand, useCardBrand } from '@/hooks/use-card-brand'
import { PaymentSessionResponse } from '@/lib/api/generated/models'

// Hardcoded US states for now - should be moved to a separate config file or fetched from an API
const US_STATES = [
  { name: 'Alabama', code: 'AL' },
  { name: 'Alaska', code: 'AK' },
  { name: 'Arizona', code: 'AZ' },
  { name: 'Arkansas', code: 'AR' },
  { name: 'California', code: 'CA' },
  { name: 'Colorado', code: 'CO' },
  { name: 'Connecticut', code: 'CT' },
  { name: 'Delaware', code: 'DE' },
  { name: 'Florida', code: 'FL' },
  { name: 'Georgia', code: 'GA' },
  { name: 'Hawaii', code: 'HI' },
  { name: 'Idaho', code: 'ID' },
  { name: 'Illinois', code: 'IL' },
  { name: 'Indiana', code: 'IN' },
  { name: 'Iowa', code: 'IA' },
  { name: 'Kansas', code: 'KS' },
  { name: 'Kentucky', code: 'KY' },
  { name: 'Louisiana', code: 'LA' },
  { name: 'Maine', code: 'ME' },
  { name: 'Maryland', code: 'MD' },
  { name: 'Massachusetts', code: 'MA' },
  { name: 'Michigan', code: 'MI' },
  { name: 'Minnesota', code: 'MN' },
  { name: 'Mississippi', code: 'MS' },
  { name: 'Missouri', code: 'MO' },
  { name: 'Montana', code: 'MT' },
  { name: 'Nebraska', code: 'NE' },
  { name: 'Nevada', code: 'NV' },
  { name: 'New Hampshire', code: 'NH' },
  { name: 'New Jersey', code: 'NJ' },
  { name: 'New Mexico', code: 'NM' },
  { name: 'New York', code: 'NY' },
  { name: 'North Carolina', code: 'NC' },
  { name: 'North Dakota', code: 'ND' },
  { name: 'Ohio', code: 'OH' },
  { name: 'Oklahoma', code: 'OK' },
  { name: 'Oregon', code: 'OR' },
  { name: 'Pennsylvania', code: 'PA' },
  { name: 'Rhode Island', code: 'RI' },
  { name: 'South Carolina', code: 'SC' },
  { name: 'South Dakota', code: 'SD' },
  { name: 'Tennessee', code: 'TN' },
  { name: 'Texas', code: 'TX' },
  { name: 'Utah', code: 'UT' },
  { name: 'Vermont', code: 'VT' },
  { name: 'Virginia', code: 'VA' },
  { name: 'Washington', code: 'WA' },
  { name: 'West Virginia', code: 'WV' },
  { name: 'Wisconsin', code: 'WI' },
  { name: 'Wyoming', code: 'WY' },
] as const

export const styles = {
  base: {
    fontFamily: 'sans-serif',
    color: '#1f2937',
    padding: '0.75rem',
  },
  '::placeholder': {
    color: '#9ca3af',
  },
}

const validationSchema = z.object({
  cardholderName: z.string().min(1, 'Cardholder name is required.'),
  email: z.string().min(1, 'Email is required.').email('Email is invalid.'),
  street: z.string().min(1, 'Street address is required.'),
  city: z.string().min(1, 'City is required.'),
  state: z.string().min(1, 'State is required.'),
  zip: z.string().min(1, 'ZIP code is required.'),
  cardNumber: z
    .object({
      complete: z.boolean(),
      error: z.any().optional(),
    })
    .refine((data) => data.complete, {
      message: 'Card number is invalid.',
    }),
  cardExpirationDate: z
    .object({
      complete: z.boolean(),
      error: z.any().optional(),
    })
    .refine((data) => data.complete, {
      message: 'Expiration date is invalid.',
    }),
  cardVerificationCode: z
    .object({
      complete: z.boolean(),
      error: z.any().optional(),
    })
    .refine((data) => data.complete, {
      message: 'CVC is invalid.',
    }),
})

const PaymentForm = ({ paymentSession }: { paymentSession: PaymentSessionResponse }) => {
  const { bt } = useBasisTheory()
  const { cardBrand, setCardBrand } = useCardBrand()
  const [apiError, setApiError] = useState<string | undefined>()

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      cardholderName: paymentSession.customer.name ?? '',
      email: paymentSession.customer.email ?? '',
      street:
        (paymentSession.billingAddress.line1 ?? '') +
        (paymentSession.billingAddress.line2 ? ' ' + paymentSession.billingAddress.line2 : ''),
      city: paymentSession.billingAddress.city ?? '',
      state: paymentSession.billingAddress.state ?? '',
      zip: paymentSession.billingAddress.postalCode ?? '',
      cardNumber: { complete: false, error: undefined },
      cardExpirationDate: { complete: false, error: undefined },
      cardVerificationCode: { complete: false, error: undefined },
    },
    mode: 'onChange',
  })

  const cardNumberElementRef = useRef<ICardNumberElement>(null)
  const cardExpirationDateElementRef = useRef<ICardExpirationDateElement>(null)
  const cardVerificationCodeElementRef = useRef<ICardVerificationCodeElement>(null)

  const onSubmit = async (data: z.infer<typeof validationSchema>) => {
    console.log('data', data)
    setApiError(undefined)

    try {
      if (
        !cardNumberElementRef.current ||
        !cardExpirationDateElementRef.current ||
        !cardVerificationCodeElementRef.current
      ) {
        console.log('One or more element refs are not initialized')
        return
      }

      const month = cardExpirationDateElementRef.current.month()
      const year = cardExpirationDateElementRef.current.year()

      if (month === undefined || year === undefined) {
        console.log('Expiration month or year is not provided')
        return
      }
      const token = await bt?.tokens.create({
        type: 'card',
        data: {
          number: cardNumberElementRef.current,
          expiration_month: month,
          expiration_year: year,
          cvc: cardVerificationCodeElementRef.current,
        },
      })
      console.log('Success! Token:', token)
    } catch (error) {
      console.error('Tokenization failed:', error)
      setApiError('Tokenization failed. Please check your card details.')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Card Details Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">Card Details</h3>
        <div>
          <label htmlFor="card-number" className="block text-sm font-medium text-gray-700 mb-1">
            Card Number
          </label>
          <Controller
            name="cardNumber"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <CardNumberInput
                  id="card-number"
                  ref={cardNumberElementRef}
                  placeholder="1234 5678 9012 3456"
                  onChange={(event) => {
                    field.onChange({
                      complete: event.complete,
                      error: event.errors?.[0],
                    })
                    if ('cardBrand' in event) {
                      setCardBrand(event.cardBrand as CardBrand)
                    }
                  }}
                />
                {fieldState.isDirty && fieldState.error && (
                  <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="card-expiry" className="block text-sm font-medium text-gray-700 mb-1">
              Expiry Date
            </label>
            <Controller
              name="cardExpirationDate"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <CardExpiryInput
                    id="card-expiry"
                    ref={cardExpirationDateElementRef}
                    placeholder="MM/YY"
                    onChange={(event) =>
                      field.onChange({
                        complete: event.complete,
                        error: event.errors?.[0],
                      })
                    }
                  />
                  {fieldState.isDirty && fieldState.error && (
                    <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                  )}
                </>
              )}
            />
          </div>
          <div>
            <label htmlFor="card-cvc" className="block text-sm font-medium text-gray-700 mb-1">
              CVV
            </label>
            <Controller
              name="cardVerificationCode"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <CardCvcInput
                    id="card-cvc"
                    ref={cardVerificationCodeElementRef}
                    placeholder="123"
                    cardBrand={cardBrand}
                    onChange={(event) =>
                      field.onChange({
                        complete: event.complete,
                        error: event.errors?.[0],
                      })
                    }
                  />
                  {fieldState.isDirty && fieldState.error && (
                    <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                  )}
                </>
              )}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="cardholder-name" className="block text-sm font-medium text-gray-700 mb-1">
              Cardholder Name
            </label>
            <Controller
              name="cardholderName"
              control={control}
              render={({ field }) => <Input id="cardholder-name" {...field} placeholder="John Doe" />}
            />
            {errors.cardholderName && <p className="mt-1 text-sm text-red-600">{errors.cardholderName.message}</p>}
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => <Input id="email" type="email" {...field} placeholder="john.doe@example.com" />}
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
          </div>
        </div>
      </div>

      {/* Billing Address Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">Billing Address</h3>
        <div>
          <label htmlFor="street-address" className="block text-sm font-medium text-gray-700 mb-1">
            Street Address
          </label>
          <Controller
            name="street"
            control={control}
            render={({ field }) => <Input id="street-address" {...field} placeholder="123 Main Street" />}
          />
          {errors.street && <p className="mt-1 text-sm text-red-600">{errors.street.message}</p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <Controller
              name="city"
              control={control}
              render={({ field }) => <Input id="city" {...field} placeholder="Little Rock" />}
            />
            {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>}
          </div>
          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
              State
            </label>
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state.code} value={state.code}>
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.state && <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>}
          </div>
          <div>
            <label htmlFor="zip-code" className="block text-sm font-medium text-gray-700 mb-1">
              ZIP Code
            </label>
            <Controller
              name="zip"
              control={control}
              render={({ field }) => <Input id="zip-code" {...field} placeholder="72201" />}
            />
            {errors.zip && <p className="mt-1 text-sm text-red-600">{errors.zip.message}</p>}
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting || !isValid}
        className="w-full rounded-md bg-blue-700 px-4 py-3 font-semibold text-white shadow-lg hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
      >
        {isSubmitting ? 'Submitting...' : 'Place Order'}
      </button>
      {apiError && <p className="mt-2 text-sm text-red-600">{apiError}</p>}
    </form>
  )
}

export default PaymentForm
